import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { TextComponent } from 'design/text/textComponents';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { RewardFormValues, RewardFormViolation } from 'types/rewardFormTypes';

type Props = {
  disabled?: boolean;
  values: RewardFormValues;
  violations: RewardFormViolation[];
  onAmountValueChange(nextValue: string): void;
  onCommentValueChange(nextValue: string): void;
};

const AMOUNT_MAX_LENGTH = 15;

const COMMENT_MAX_LENGTH = 255;

export const RewardFieldset = (props: Props) => {
  const {
    disabled,
    values,
    violations,
    onAmountValueChange,
    onCommentValueChange,
  } = props;

  const intl = useIntlDictionary();

  const { rewardFieldset: ownDictionary } = intl;

  const amountHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'AmountMissed':
          return intl.inputValidationRequired;
        case 'AmountExceedsBalance':
          return ownDictionary.amountExceedsBalanceViolation;
      }
    }
  }, [
    intl.inputValidationRequired,
    ownDictionary.amountExceedsBalanceViolation,
    violations,
  ]);

  const commentHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'CommentMissed':
          return intl.inputValidationRequired;
      }
    }
  }, [intl.inputValidationRequired, violations]);

  return (
    <FieldsetLayoutComponent>
      <FloatingLabelComponent
        lifted={!!values.amount}
        invalid={!!amountHint}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.amountInputLabel}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {amountHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="amount"
          autoFocus
          value={values.amount}
          disabled={disabled}
          invalid={!!amountHint}
          maxLength={AMOUNT_MAX_LENGTH}
          onValueChange={onAmountValueChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.comment}
        invalid={!!commentHint}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.commentInputLabel}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {commentHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="comment"
          multiLine
          value={values.comment}
          disabled={disabled}
          invalid={!!commentHint}
          maxLength={COMMENT_MAX_LENGTH}
          onValueChange={onCommentValueChange}
        />
      </FloatingLabelComponent>
    </FieldsetLayoutComponent>
  );
};
